/**
 * configDsl.js — bidirectional sync between the ConfigForm React state and a
 * `mbd.feedConfig(...)` DSL string.
 *
 *   generateDsl(form, { algosById }) -> string
 *   parseDsl(string,  { algosByName }) -> { ok: true, value } | { ok: false, error }
 *
 * The form state mirrors the JSONB `feed_config` row shape one-for-one (with
 * the exclusion rules fragmented into named slots to match the existing UI):
 *
 *   name, description, isPublic
 *   pageSize, buildingTimeoutMs
 *   mainAlgos: [{ algoId, weight }, ...], mainMaxCacheAgeSec
 *   primaryFallback (algo_id | ''), primaryMaxCacheAgeSec
 *   secondaryFallback (algo_id | ''), secondaryMaxCacheAgeSec
 *   exclusionMaxItems        { enabled, field, value }
 *   exclusionMinDistance     { enabled, field, distance }
 *   exclusionHide1           { enabled, field }
 *   exclusionHide2           { enabled, field }
 *   exclusionItemsShownNTimes        { enabled, value }
 *   exclusionItemsShownLastNSeconds  { enabled, value }
 *
 * The parser runs the DSL inside `new Function('mbd', code)` with a stubbed
 * mbd that only records builder calls. No network, no real SDK.
 */

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_BUILDING_TIMEOUT_MS = 250;
const DEFAULT_MAIN_CACHE_S = 7200;
const DEFAULT_PRIMARY_CACHE_S = 7200;
const DEFAULT_SECONDARY_CACHE_S = 600;

function algoRef(id, algosById) {
  // Prefer human-readable name; fall back to numeric id if the algo isn't
  // in the cheatsheet (deleted / unknown).
  const name = algosById?.get?.(Number(id))?.name;
  return name ? JSON.stringify(name) : String(Number(id));
}

export function generateDsl(form, { algosById } = {}) {
  const name = form?.name || 'my-config';
  const lines = [`mbd.feedConfig(${JSON.stringify(name)})`];

  if (form?.description) lines.push(`  .description(${JSON.stringify(form.description)})`);

  if (form?.pageSize != null && form.pageSize !== '' && Number(form.pageSize) !== DEFAULT_PAGE_SIZE) {
    lines.push(`  .pageSize(${Number(form.pageSize)})`);
  }

  if (Array.isArray(form?.mainAlgos) && form.mainAlgos.length > 0) {
    const items = form.mainAlgos.map((c) => {
      const ref = algoRef(c.algoId ?? c.algo_id, algosById);
      const w = c.weight ?? 1;
      return `{ algoId: ${ref}, weight: ${w} }`;
    });
    lines.push(`  .mainAlgos([${items.join(', ')}])`);
  }

  if (form?.mainMaxCacheAgeSec != null && form.mainMaxCacheAgeSec !== '' && Number(form.mainMaxCacheAgeSec) !== DEFAULT_MAIN_CACHE_S) {
    lines.push(`  .mainMaxCacheAgeS(${Number(form.mainMaxCacheAgeSec)})`);
  }
  if (form?.buildingTimeoutMs != null && form.buildingTimeoutMs !== '' && Number(form.buildingTimeoutMs) !== DEFAULT_BUILDING_TIMEOUT_MS) {
    lines.push(`  .buildingTimeoutMs(${Number(form.buildingTimeoutMs)})`);
  }

  if (form?.primaryFallback !== '' && form?.primaryFallback != null) {
    lines.push(`  .primaryFallback(${algoRef(form.primaryFallback, algosById)})`);
    if (form?.primaryMaxCacheAgeSec != null && form.primaryMaxCacheAgeSec !== '' && Number(form.primaryMaxCacheAgeSec) !== DEFAULT_PRIMARY_CACHE_S) {
      lines.push(`  .primaryMaxCacheAgeS(${Number(form.primaryMaxCacheAgeSec)})`);
    }
  }
  if (form?.secondaryFallback !== '' && form?.secondaryFallback != null) {
    lines.push(`  .secondaryFallback(${algoRef(form.secondaryFallback, algosById)})`);
    if (form?.secondaryMaxCacheAgeSec != null && form.secondaryMaxCacheAgeSec !== '' && Number(form.secondaryMaxCacheAgeSec) !== DEFAULT_SECONDARY_CACHE_S) {
      lines.push(`  .secondaryMaxCacheAgeS(${Number(form.secondaryMaxCacheAgeSec)})`);
    }
  }

  if (form?.exclusionMaxItems?.enabled && form.exclusionMaxItems.field) {
    lines.push(`  .maxItemsWithSameField(${JSON.stringify(form.exclusionMaxItems.field)}, ${Number(form.exclusionMaxItems.value) || 1})`);
  }
  if (form?.exclusionMinDistance?.enabled && form.exclusionMinDistance.field) {
    lines.push(`  .minDistanceBetweenSameField(${JSON.stringify(form.exclusionMinDistance.field)}, ${Number(form.exclusionMinDistance.distance) || 1})`);
  }
  if (form?.exclusionHide1?.enabled && form.exclusionHide1.field) {
    lines.push(`  .hideItemsWithSameField(${JSON.stringify(form.exclusionHide1.field)})`);
  }
  if (form?.exclusionHide2?.enabled && form.exclusionHide2.field) {
    lines.push(`  .hideItemsWithSameField(${JSON.stringify(form.exclusionHide2.field)})`);
  }
  if (form?.exclusionItemsShownNTimes?.enabled) {
    lines.push(`  .excludeItemsShownNTimes(${Number(form.exclusionItemsShownNTimes.value) || 1})`);
  }
  if (form?.exclusionItemsShownLastNSeconds?.enabled) {
    lines.push(`  .excludeItemsShownLastNSeconds(${Number(form.exclusionItemsShownLastNSeconds.value) || 3600})`);
  }

  if (form?.isPublic) lines.push(`  .isPublic(true)`);

  lines.push(`  .save();`);
  return lines.join('\n');
}

export function parseDsl(code, { algosByName } = {}) {
  const value = {
    name: '',
    description: '',
    pageSize: DEFAULT_PAGE_SIZE,
    mainAlgos: [],
    mainMaxCacheAgeSec: DEFAULT_MAIN_CACHE_S,
    buildingTimeoutMs: DEFAULT_BUILDING_TIMEOUT_MS,
    primaryFallback: '',
    primaryMaxCacheAgeSec: DEFAULT_PRIMARY_CACHE_S,
    secondaryFallback: '',
    secondaryMaxCacheAgeSec: DEFAULT_SECONDARY_CACHE_S,
    exclusionMaxItems: { enabled: false, field: '', value: 1 },
    exclusionMinDistance: { enabled: false, field: '', distance: 1 },
    exclusionHide1: { enabled: false, field: '' },
    exclusionHide2: { enabled: false, field: '' },
    exclusionItemsShownNTimes: { enabled: false, value: 1 },
    exclusionItemsShownLastNSeconds: { enabled: false, value: 3600 },
    isPublic: false,
  };
  const unresolvedNames = [];
  let hideCount = 0;

  function resolveAlgoIdLike(x) {
    if (typeof x === 'number' && Number.isInteger(x) && x > 0) return x;
    if (typeof x === 'string') {
      const trimmed = x.trim();
      const numeric = parseInt(trimmed, 10);
      if (!Number.isNaN(numeric) && String(numeric) === trimmed) return numeric;
      const hit = algosByName?.get?.(trimmed);
      if (hit?.algo_id) return Number(hit.algo_id);
      unresolvedNames.push(trimmed);
    }
    return null;
  }

  let touched = false;
  const stub = {
    description(s) { value.description = typeof s === 'string' ? s : ''; return stub; },
    pageSize(n) { value.pageSize = Number(n) || DEFAULT_PAGE_SIZE; return stub; },
    mainAlgos(arr) {
      if (!Array.isArray(arr)) return stub;
      const out = [];
      for (const a of arr) {
        if (!a || typeof a !== 'object') continue;
        const id = resolveAlgoIdLike(a.algoId ?? a.algo_id);
        if (id == null) continue;
        out.push({ algo_id: id, weight: Number(a.weight ?? 1) || 1 });
      }
      value.mainAlgos = out;
      return stub;
    },
    mainMaxCacheAgeS(n) { value.mainMaxCacheAgeSec = Number(n) || DEFAULT_MAIN_CACHE_S; return stub; },
    buildingTimeoutMs(n) { value.buildingTimeoutMs = Number(n) || DEFAULT_BUILDING_TIMEOUT_MS; return stub; },
    primaryFallback(id) {
      const r = resolveAlgoIdLike(id);
      if (r != null) value.primaryFallback = String(r);
      return stub;
    },
    primaryMaxCacheAgeS(n) { value.primaryMaxCacheAgeSec = Number(n) || DEFAULT_PRIMARY_CACHE_S; return stub; },
    secondaryFallback(id) {
      const r = resolveAlgoIdLike(id);
      if (r != null) value.secondaryFallback = String(r);
      return stub;
    },
    secondaryMaxCacheAgeS(n) { value.secondaryMaxCacheAgeSec = Number(n) || DEFAULT_SECONDARY_CACHE_S; return stub; },
    maxItemsWithSameField(field, v) {
      value.exclusionMaxItems = { enabled: true, field: String(field || ''), value: Number(v) || 1 };
      return stub;
    },
    minDistanceBetweenSameField(field, d) {
      value.exclusionMinDistance = { enabled: true, field: String(field || ''), distance: Number(d) || 1 };
      return stub;
    },
    hideItemsWithSameField(field) {
      const slot = hideCount === 0 ? 'exclusionHide1' : 'exclusionHide2';
      value[slot] = { enabled: true, field: String(field || '') };
      hideCount += 1;
      return stub;
    },
    excludeItemsShownNTimes(v) {
      value.exclusionItemsShownNTimes = { enabled: true, value: Number(v) || 1 };
      return stub;
    },
    excludeItemsShownLastNSeconds(v) {
      value.exclusionItemsShownLastNSeconds = { enabled: true, value: Number(v) || 3600 };
      return stub;
    },
    isPublic(b) { value.isPublic = Boolean(b); return stub; },
    save() { return stub; },
    update() { return stub; },
    describe() { return stub; },
  };

  const mbd = {
    feedConfig(name) {
      touched = true;
      value.name = typeof name === 'string' ? name : '';
      return stub;
    },
  };

  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('mbd', code);
    fn(mbd);
  } catch (e) {
    return { ok: false, error: e?.message ?? String(e) };
  }

  if (!touched) return { ok: false, error: 'no mbd.feedConfig(...) call found' };
  if (unresolvedNames.length > 0) {
    return { ok: false, error: `algo name(s) not found in your saved algos: ${unresolvedNames.join(', ')}` };
  }
  return { ok: true, value };
}
